/**
 * web/src/components/shared/Card.tsx
 * Card composition with Head/Title/Body/Foot subcomponents. Matches `.card`
 * (planning-docs/design_handoff_pixel_dashboard/design_files/styles.css
 * lines 609-636): white bg, 1px border, 8px radius, head 12×16 with
 * 1px bottom border, body 16px, foot 10×16 on bg-soft.
 *
 * Used by: WorkflowDetail context panel, GateReview composition, Event
 * Management cards, Stats banner detail variants.
 */
import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * Root Card container. White bg + 1px border + 8px radius (rounded-lg) +
 * overflow-hidden so subcomponent borders/edges stay tucked inside.
 */
export function Card({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('bg-bg border border-border rounded-lg overflow-hidden', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** Head: 12×16 padding, 1px bottom border, flex row with 10px gap. */
function Head({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-2.5 px-4 py-3 border-b border-border', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** Title: 13/600 -tracking-0.005em (handoff Typography). */
function Title({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('text-[13px] font-semibold tracking-tight', className)} {...props}>
      {children}
    </div>
  );
}

/** Body: 16px padding all-around. */
function Body({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-4', className)} {...props}>
      {children}
    </div>
  );
}

/** Foot: 10×16 padding on bg-soft + 1px top border. */
function Foot({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 bg-bg-soft border-t border-border',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

Card.Head = Head;
Card.Title = Title;
Card.Body = Body;
Card.Foot = Foot;
